import { FikoYTId } from '@/src/service/fikoyt-ui/helpers/FikoYTId';

export type SablonMod = 'blok' | 'serbest' | 'hibrit';
export type ArkaplanTur = 'renk' | 'gradient' | 'resim' | 'hazir';
export type ElementTur =
  | 'sabit-metin' | 'parametre-metin' | 'parametre-render' | 'markdown'
  | 'sekil' | 'resim'
  | 'sayac' | 'ilerleme-cubugu' | 'ilerleme-halka' | 'gauge'
  | 'sparkline' | 'bar-grafik' | 'pasta-grafik' | 'alan-grafik'
  | 'radar-grafik' | 'isi-haritasi' | 'seviye-cubugu'
  | 'dijital-saat' | 'analog-saat' | 'countdown' | 'kronometr' | 'zaman-cizelgesi'
  | 'durum-dot' | 'durum-kart' | 'led-gosterge' | 'trafik-isigi' | 'sinyal-cubugu'
  | 'liste-tekrar' | 'siralama-listesi' | 'mini-tablo' | 'kanban-sutun'
  | 'avatar' | 'badge' | 'ticker' | 'qr-kod' | 'hava-durumu' | 'lottie' | 'iframe'
  | 'buton' | 'toggle' | 'slider' | 'tab-grup';
export type SekilTuru = 'dikdortgen' | 'daire' | 'ucgen' | 'cizgi' | 'yildiz';

export interface ISablonStil {
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  color?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  borderRadius?: number;
  opacity?: number;
  rotate?: number;
  shadow?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'center' | 'bottom';
  letterSpacing?: number;
  lineHeight?: number;
}

export interface IArkaPlan {
  tur: ArkaplanTur;
  deger: string;
}

export interface IBolge {
  id: string;
  yukseklik: number | 'auto';
}

export type GirisAnimasyon = 'fadeIn' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'scaleIn' | 'bounce' | 'flip' | 'rotateIn';
export type SurekliAnimasyon = 'pulse' | 'float' | 'rotate' | 'shimmer' | 'blink' | 'shake' | 'swing' | 'breathe' | 'glow';
export type HoverAnimasyon = 'scale' | 'glow' | 'shake' | 'jello' | 'lift' | 'colorShift';
export type TiklamaAnimasyon = 'pop' | 'ripple' | 'shrink' | 'flash' | 'rubber' | 'tada' | 'heartbeat';

export interface ISablonAnimasyon {
  giris?: GirisAnimasyon;
  girisSure?: number;
  girisGecikme?: number;
  surekli?: SurekliAnimasyon;
  surekliSure?: number;
  hover?: HoverAnimasyon;
  tiklama?: TiklamaAnimasyon;
}

export interface ISablonElement {
  id: string;
  tur: ElementTur;
  x: number;
  y: number;
  genislik: number;
  yukseklik: number;
  bolgeId?: string;
  parametreAnahtari?: string;
  icerik?: string;
  sekilTuru?: SekilTuru;
  resimUrl?: string;
  stil: ISablonStil;
  animasyon?: ISablonAnimasyon;
  katman?: number;
  gorunur?: boolean;
  deger?: number;
  min?: number;
  max?: number;
  renk?: string;
  veriler?: number[];
  etiketler?: string[];
  tiklanmaKodu?: string;
  format?: string;
}

export interface ISablonVeriKaynagi {
  mod: 'aggregate' | 'sabit' | 'soket' | 'hesaplama';
  vt?: string;
  pipeline?: any[];
  donusumFunc?: string;
  sabitVeri?: any;
  soketOlay?: string;
  soketFiltre?: Record<string, any>;
  piFunc?: string;
  yenilemeAraligi?: number;
  soketTetik?: string;
}

export interface ISablonOlaylar {
  tiklanma?: string;
  yenilendi?: string;
  esikAsimi?: string;
  ciftTiklama?: string;
  [key: string]: string | undefined;
}

export interface ISablonItem {
  id: string;
  sablon?: ISablon;
  veriKaynagi?: ISablonVeriKaynagi;
  olaylar?: ISablonOlaylar;
  [key: string]: any;
}

export interface ISablonPozisyon {
  genislik: number;
  yukseklik: number;
  x?: number;
  y?: number;
  sira?: number;
}

export interface ISablonGorunumStil {
  arkaPlan?: string;
  gradyan?: string;
  renkYazi?: string;
  borderRadius?: number;
  animasyon?: string;
  golge?: string;
}

export interface IDashboardAyarlar {
  layout: 'grid' | 'serbest';
  kolonSayisi?: number;
  aralik?: number;
  widgetler: ISablon[];
}

export interface ISablon {
  mod: SablonMod;
  arkaPlan: IArkaPlan;
  bolgeler: IBolge[];
  elementler: ISablonElement[];
  genislik?: number;
  yukseklik?: number;
  htmlModu?: boolean;
  htmlIcerik?: string;
  cssIcerik?: string;
  jsIcerik?: string;

  id?: string;
  tur?: 'widget' | 'kart' | 'serbest';
  baslik?: string;
  simge?: string;
  aciklama?: string;
  gorunur?: boolean;
  silindi?: boolean;
  yetki?: string[];
  sira?: number;
  kaynak?: 'kod' | 'db';

  pozisyon?: ISablonPozisyon;
  stil?: ISablonGorunumStil;

  veriKaynagi?: ISablonVeriKaynagi;
  olaylar?: ISablonOlaylar;
  itemler?: ISablonItem[];
}

const ELEMENT_VARSAYILANLAR: Partial<Record<ElementTur, Partial<ISablonElement>>> = {
  'sabit-metin': { icerik: 'Metin', genislik: 120, yukseklik: 30, stil: { fontSize: 14, color: '#ffffff' } },
  'parametre-metin': { genislik: 120, yukseklik: 30, stil: { fontSize: 14, color: '#ffffff' } },
  'parametre-render': { genislik: 120, yukseklik: 30, stil: { fontSize: 14, color: '#ffffff' } },
  'markdown': { icerik: '**Markdown**', genislik: 160, yukseklik: 60, stil: { fontSize: 14 } },
  'sekil': { genislik: 80, yukseklik: 80, sekilTuru: 'dikdortgen', stil: { fill: '#e6f7ff', stroke: '#1890ff', strokeWidth: 1 } },
  'resim': { genislik: 100, yukseklik: 100, resimUrl: '🖼' },
  'buton': { icerik: 'Buton', genislik: 100, yukseklik: 36 },
  'sayac': { genislik: 80, yukseklik: 50, deger: 42 },
  'gauge': { genislik: 100, yukseklik: 100, deger: 65 },
  'dijital-saat': { genislik: 120, yukseklik: 40 },
  'analog-saat': { genislik: 100, yukseklik: 100 },
  'durum-dot': { genislik: 24, yukseklik: 24, deger: 1 },
  'led-gosterge': { genislik: 30, yukseklik: 30, deger: 1 },
  'ilerleme-cubugu': { genislik: 160, yukseklik: 20, deger: 50 },
  'ilerleme-halka': { genislik: 80, yukseklik: 80, deger: 65 },
  'sparkline': { genislik: 120, yukseklik: 40, veriler: [10, 30, 20, 50, 40, 60] },
  'bar-grafik': { genislik: 160, yukseklik: 100, veriler: [30, 50, 20, 70], etiketler: ['A', 'B', 'C', 'D'] },
  'badge': { icerik: 'Yeni', genislik: 60, yukseklik: 24 },
  'ticker': { icerik: 'Kayan metin', genislik: 200, yukseklik: 28 },
  'toggle': { genislik: 80, yukseklik: 30 },
  'slider': { genislik: 160, yukseklik: 30, deger: 50 },
};

export function yeniSablonElement(tur: ElementTur, parcali?: Partial<ISablonElement>): ISablonElement {
  const varsayilan = ELEMENT_VARSAYILANLAR[tur];
  return {
    id: new FikoYTId().toString(),
    tur,
    x: 10,
    y: 10,
    genislik: 100,
    yukseklik: 30,
    gorunur: true,
    katman: 0,
    ...varsayilan,
    ...parcali,
    stil: { ...(varsayilan?.stil ?? {}), ...(parcali?.stil ?? {}) },
  };
}

export function bosISablon(): ISablon {
  return {
    mod: 'hibrit',
    tur: 'kart',
    gorunur: true,
    arkaPlan: { tur: 'renk', deger: '#ffffff' },
    bolgeler: [
      { id: 'ust', yukseklik: 60 },
      { id: 'orta', yukseklik: 'auto' },
      { id: 'alt', yukseklik: 40 },
    ],
    elementler: [],
  };
}

export const HAZIR_ARKAPLANLAR: { anahtar: string; baslik: string; arkaPlan: IArkaPlan }[] = [
  { anahtar: 'karanlik-mavi', baslik: 'Kurumsal', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #0f2027, #203a43, #2c5364)' } },
  { anahtar: 'neon-yesil', baslik: 'Teknoloji', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #0a0a0a, #1a3a1a, #00ff41)' } },
  { anahtar: 'sicak-turuncu', baslik: 'Enerji', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #f12711, #f5af19)' } },
  { anahtar: 'mor-pembe', baslik: 'Yaratıcı', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #7b2ff7, #ff2d95)' } },
  { anahtar: 'okyanus', baslik: 'Okyanus', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #0077b6, #00b4d8, #90e0ef)' } },
  { anahtar: 'gece', baslik: 'Gece', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #0d1b2a, #1b2838, #415a77)' } },
  { anahtar: 'gunbatimi', baslik: 'Günbatımı', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #ee9ca7, #ffdde1, #f6d365)' } },
  { anahtar: 'orman', baslik: 'Orman', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #134e5e, #2d6a4f, #52b788)' } },
  { anahtar: 'sade-beyaz', baslik: 'Minimal', arkaPlan: { tur: 'renk', deger: '#ffffff' } },
  { anahtar: 'koyu-gri', baslik: 'Karanlık', arkaPlan: { tur: 'renk', deger: '#1a1a2e' } },
  { anahtar: 'krem', baslik: 'Krem', arkaPlan: { tur: 'renk', deger: '#faf3e0' } },
  { anahtar: 'buz', baslik: 'Buz', arkaPlan: { tur: 'gradient', deger: 'linear-gradient(135deg, #e0eafc, #cfdef3)' } },
];
